import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './LiksModalBackdrop.css';

export type LiksModalBackdropProps = {
  className?: string;
  onDismiss: () => void;
  children: ReactNode;
};

/**
 * Nested backdrops register here so Escape only dismisses the topmost overlay.
 */
const escapeDismissStack: Array<() => void> = [];

/**
 * Overlay that dismisses only when both pointer down and click land on the
 * backdrop itself — not when a drag starts inside the dialog and ends outside.
 * Escape dismisses the topmost open backdrop via `onDismiss`.
 * Always portals to document.body so it stacks above the app sidebar.
 */
export function LiksModalBackdrop({
  className,
  onDismiss,
  children,
}: LiksModalBackdropProps) {
  const pressedOnBackdropRef = useRef(false);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const dismiss = () => {
      onDismissRef.current();
    };
    escapeDismissStack.push(dismiss);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (escapeDismissStack[escapeDismissStack.length - 1] !== dismiss) {
        return;
      }
      event.preventDefault();
      dismiss();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = escapeDismissStack.lastIndexOf(dismiss);
      if (index >= 0) {
        escapeDismissStack.splice(index, 1);
      }
    };
  }, []);

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    pressedOnBackdropRef.current = event.target === event.currentTarget;
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && pressedOnBackdropRef.current) {
      onDismiss();
    }
    pressedOnBackdropRef.current = false;
  };

  return createPortal(
    <div
      className={['liks-modal-backdrop', className].filter(Boolean).join(' ')}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {children}
    </div>,
    document.body,
  );
}
